import { useEffect, useMemo, useState } from 'react';

import {
  getAgentModelName,
  useGetAgentModelOptionsQuery,
  useGetProjectsQuery,
  useSelectedProjectId,
} from 'features/projects';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import { useCloneProjectMutation, useGetMasterWithSubsQuery } from '../api/master.api';
import {
  ClonePayload,
  CloneResponse,
  MIGRATION_ENTITIES,
  MIGRATION_ENTITY_LABELS,
  MigrationEntity,
} from '../api/types';

type Mode = 'same-master' | 'different-master';

const SAME_MASTER_EXCLUDED: ReadonlyArray<MigrationEntity> = ['subProjects', 'targetProject'];

const extractErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const { data } = e as { data: unknown };
    if (typeof data === 'string') return data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const msg = (data as { message: unknown }).message;
      if (typeof msg === 'string') return msg;
    }
  }
  return fallback;
};

const STATUS_CLASS: Record<string, string> = {
  completed: 'text-success',
  error: 'text-destructive',
  executing: 'text-text-muted',
  validating: 'text-text-muted',
  pending: 'text-text-muted',
  verifying: 'text-text-muted',
};

interface EntityCheckboxGridProps {
  available: ReadonlyArray<MigrationEntity>;
  selected: MigrationEntity[];
  onToggle: (entity: MigrationEntity) => void;
}

const EntityCheckboxGrid = ({ available, selected, onToggle }: EntityCheckboxGridProps) => {
  const isAllSelected = selected.includes('allProperties');
  return (
    <div className="space-y-2">
      <Label>Entidades a migrar</Label>
      <div className="grid gap-2 md:grid-cols-2">
        {available.map((entity) => (
          <label
            key={entity}
            htmlFor={`clone-entity-${entity}`}
            className={cn(
              'flex items-center gap-2 rounded-md border border-border p-2 text-sm',
              isAllSelected && entity !== 'allProperties' && 'opacity-50'
            )}
          >
            <Checkbox
              id={`clone-entity-${entity}`}
              checked={selected.includes(entity)}
              disabled={isAllSelected && entity !== 'allProperties'}
              onCheckedChange={() => onToggle(entity)}
            />
            <span>{MIGRATION_ENTITY_LABELS[entity]}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SummaryItem = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-md border border-border p-2">
    <p className="text-xs text-text-muted">{label}</p>
    <p className="text-base font-semibold">{value}</p>
  </div>
);

interface CloneResultPanelProps {
  result: CloneResponse;
}

const CloneResultPanel = ({ result }: CloneResultPanelProps) => (
  <section className="space-y-3 rounded-md border border-border p-6">
    <header className="flex items-center justify-between">
      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-text-muted">Resultado</h3>
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-xs font-medium',
          result.success ? 'bg-success-light text-success' : 'bg-destructive/10 text-destructive'
        )}
      >
        {result.success ? 'OK' : 'Con errores'}
      </span>
    </header>
    <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
      <SummaryItem label="Pasos totales" value={result.summary.totalSteps} />
      <SummaryItem label="Completados" value={result.summary.completedSteps} />
      <SummaryItem label="Fallidos" value={result.summary.failedSteps} />
      <SummaryItem label="Creados" value={result.summary.objectsCreated} />
      <SummaryItem label="Actualizados" value={result.summary.objectsUpdated} />
      <SummaryItem label="Omitidos" value={result.summary.objectsSkipped} />
    </div>
    <ul className="divide-y divide-border rounded-md border border-border">
      {result.steps.map((step) => (
        <li key={`${step.entity}-${step.createdAt}`} className="p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium">{MIGRATION_ENTITY_LABELS[step.entity] ?? step.entity}</span>
            <span className={cn('text-xs uppercase', STATUS_CLASS[step.status] ?? 'text-text-muted')}>
              {step.status}
            </span>
          </div>
          <p className="text-xs text-text-muted">{step.message}</p>
        </li>
      ))}
    </ul>
  </section>
);

const CloneTab = () => {
  const selectedId = useSelectedProjectId();
  const { data: allProjects = [] } = useGetProjectsQuery();

  const currentMasterId = useMemo(() => {
    if (selectedId == null) return null;
    const proj = allProjects.find((p) => p.id === selectedId);
    if (!proj) return selectedId;
    return proj.masterProjectId ?? proj.id;
  }, [allProjects, selectedId]);

  const { data: master } = useGetMasterWithSubsQuery(currentMasterId ?? 0, {
    skip: currentMasterId == null,
  });

  const initialMode: Mode = useMemo(() => {
    if (selectedId == null) return 'same-master';
    const proj = allProjects.find((p) => p.id === selectedId);
    return proj?.masterProjectId ? 'same-master' : 'different-master';
  }, [allProjects, selectedId]);

  const [mode, setMode] = useState<Mode>(initialMode);
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  // Same-master state
  const [sameSourceId, setSameSourceId] = useState<number | null>(null);
  const [newSubName, setNewSubName] = useState('');
  const [sameEntities, setSameEntities] = useState<MigrationEntity[]>([]);

  // Different-master state
  const [diffSourceMasterId, setDiffSourceMasterId] = useState<number | null>(null);
  const [createNewTarget, setCreateNewTarget] = useState(false);
  const [targetMasterId, setTargetMasterId] = useState<number | null>(null);
  const [newTargetName, setNewTargetName] = useState('');
  const [newTargetPhone, setNewTargetPhone] = useState('');
  const [newTargetModelSource, setNewTargetModelSource] = useState<'default' | 'source'>('default');
  const [diffSubIds, setDiffSubIds] = useState<number[]>([]);
  const [diffEntities, setDiffEntities] = useState<MigrationEntity[]>([]);

  const [result, setResult] = useState<CloneResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cloneProject, { isLoading }] = useCloneProjectMutation();

  useEffect(() => {
    if (master && diffSourceMasterId == null) {
      setDiffSourceMasterId(master.id);
    }
  }, [master, diffSourceMasterId]);

  useEffect(() => {
    if (sameSourceId == null) return;
    const source = master?.subProjects?.find((s) => s.id === sameSourceId);
    if (source) setNewSubName(`${source.name} (copia)`);
  }, [sameSourceId, master]);

  const masterProjects = useMemo(() => allProjects.filter((p) => p.masterProjectId == null), [allProjects]);

  const subProjectsOfMaster = master?.subProjects ?? [];

  const diffSourceMaster = useMemo(
    () => allProjects.find((p) => p.id === diffSourceMasterId) ?? null,
    [allProjects, diffSourceMasterId]
  );
  const diffSourceSubs = diffSourceMaster?.subProjects ?? [];
  const { data: sourceModelOptions } = useGetAgentModelOptionsQuery(diffSourceMasterId ?? 0, {
    skip: diffSourceMasterId == null || !createNewTarget,
  });

  const newProjectDefaultModel = sourceModelOptions?.options.find((option) => option.isNewProjectDefault);
  const sourceSubForCopy = subProjectsOfMaster.find((s) => s.id === sameSourceId);
  const namePlaceholder = sourceSubForCopy ? `${sourceSubForCopy.name} (copia)` : '';

  const toggleEntity = (
    list: MigrationEntity[],
    setList: (next: MigrationEntity[]) => void,
    entity: MigrationEntity
  ) => {
    if (entity === 'allProperties') {
      setList(list.includes('allProperties') ? [] : ['allProperties']);
      return;
    }
    if (list.includes('allProperties')) return;
    setList(list.includes(entity) ? list.filter((e) => e !== entity) : [...list, entity]);
  };

  const toggleSub = (id: number) => {
    setDiffSubIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleDiffEntity = (entity: MigrationEntity) => {
    if (entity === 'allProperties') {
      const turningOn = !diffEntities.includes('allProperties');
      setDiffEntities(turningOn ? ['allProperties'] : []);
      setDiffSubIds(turningOn ? diffSourceSubs.map((s) => s.id) : []);
      return;
    }
    toggleEntity(diffEntities, setDiffEntities, entity);
  };

  const runClone = async (dryRun: boolean) => {
    setError(null);
    setResult(null);
    try {
      let payload: ClonePayload;
      if (mode === 'same-master') {
        if (sameSourceId == null) {
          setError('Selecciona el subproyecto origen.');
          return;
        }
        if (sameEntities.length === 0) {
          setError('Selecciona al menos una entidad a migrar.');
          return;
        }
        payload = {
          mode: 'same-master',
          sourceProjectId: sameSourceId,
          newSubProjectName: newSubName.trim() || undefined,
          entitiesToMigrate: sameEntities,
          dryRun,
        };
      } else {
        if (diffSourceMasterId == null) {
          setError('Selecciona la inmobiliaria origen.');
          return;
        }
        if (diffEntities.length === 0) {
          setError('Selecciona al menos una entidad a migrar.');
          return;
        }
        if (createNewTarget) {
          if (!newTargetName.trim()) {
            setError('El nombre de la nueva inmobiliaria es obligatorio.');
            return;
          }
        } else if (targetMasterId == null) {
          setError('Selecciona la inmobiliaria destino.');
          return;
        }
        payload = {
          mode: 'different-master',
          sourceProjectId: diffSourceMasterId,
          ...(createNewTarget
            ? {
                targetProject: {
                  name: newTargetName.trim(),
                  phoneNumber: newTargetPhone.trim() || undefined,
                  modelSource: newTargetModelSource,
                },
              }
            : { targetProjectId: targetMasterId ?? undefined }),
          subProjectIds: diffSubIds.length > 0 ? diffSubIds : undefined,
          entitiesToMigrate: diffEntities,
          dryRun,
        };
      }

      const response = await cloneProject(payload).unwrap();
      setResult(response);
    } catch (e) {
      setError(extractErrorMessage(e, 'No se pudo ejecutar la clonación.'));
    }
  };

  if (currentMasterId == null) {
    return (
      <section>
        <p className="text-sm text-text-muted">Selecciona una inmobiliaria desde el sidebar.</p>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
        <TabsList>
          <TabsTrigger value="same-master">Mismo master</TabsTrigger>
          <TabsTrigger value="different-master">Otra inmobiliaria</TabsTrigger>
        </TabsList>

        <TabsContent value="same-master" className="mt-4">
          <div className="space-y-4 rounded-md border border-border p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="same-source">Subproyecto origen</Label>
                <Select value={sameSourceId?.toString() ?? ''} onValueChange={(v) => setSameSourceId(Number(v))}>
                  <SelectTrigger id="same-source">
                    <SelectValue placeholder="Selecciona un subproyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {subProjectsOfMaster.map((sub) => (
                      <SelectItem key={sub.id} value={sub.id.toString()}>
                        {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="new-sub-name">Nombre nuevo</Label>
                <Input
                  id="new-sub-name"
                  value={newSubName}
                  placeholder={namePlaceholder || 'Nombre del subproyecto copiado'}
                  onChange={(e) => setNewSubName(e.target.value)}
                />
                <p className="text-xs text-text-muted">
                  Si lo dejas vacío, se usará &quot;
                  {namePlaceholder || '<nombre> (copia)'}
                  &quot;.
                </p>
              </div>
            </div>

            <EntityCheckboxGrid
              available={MIGRATION_ENTITIES.filter((e) => !SAME_MASTER_EXCLUDED.includes(e))}
              selected={sameEntities}
              onToggle={(e) => toggleEntity(sameEntities, setSameEntities, e)}
            />
          </div>
        </TabsContent>

        <TabsContent value="different-master" className="mt-4">
          <div className="space-y-4 rounded-md border border-border p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="diff-source">Inmobiliaria origen</Label>
                <Select
                  value={diffSourceMasterId?.toString() ?? ''}
                  onValueChange={(v) => setDiffSourceMasterId(Number(v))}
                >
                  <SelectTrigger id="diff-source">
                    <SelectValue placeholder="Selecciona una inmobiliaria" />
                  </SelectTrigger>
                  <SelectContent>
                    {masterProjects.map((m) => (
                      <SelectItem key={m.id} value={m.id.toString()}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-3 self-end">
                <div>
                  <Label htmlFor="create-new-target" className="block">
                    Crear inmobiliaria destino nueva
                  </Label>
                  <p className="text-xs text-text-muted">Si está apagado, se usa una inmobiliaria existente.</p>
                </div>
                <Switch id="create-new-target" checked={createNewTarget} onCheckedChange={setCreateNewTarget} />
              </div>

              {createNewTarget ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="new-target-name">Nombre destino</Label>
                    <Input
                      id="new-target-name"
                      value={newTargetName}
                      onChange={(e) => setNewTargetName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="new-target-phone">Teléfono destino</Label>
                    <Input
                      id="new-target-phone"
                      value={newTargetPhone}
                      onChange={(e) => setNewTargetPhone(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="new-target-model">Modelo IA</Label>
                    <Select
                      value={newTargetModelSource}
                      onValueChange={(v) => setNewTargetModelSource(v as 'default' | 'source')}
                    >
                      <SelectTrigger id="new-target-model">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">
                          Modelo por defecto de proyectos nuevos
                          {newProjectDefaultModel ? ` (${newProjectDefaultModel.name})` : ''}
                        </SelectItem>
                        <SelectItem value="source">
                          Mismo modelo que el origen
                          {sourceModelOptions
                            ? ` (${getAgentModelName(sourceModelOptions.currentModel, sourceModelOptions.options)})`
                            : ''}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-text-muted">
                      Los subproyectos copiados usan este modelo, salvo los que ya tienen un modelo propio.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <Label htmlFor="target-master">Inmobiliaria destino</Label>
                  <Select value={targetMasterId?.toString() ?? ''} onValueChange={(v) => setTargetMasterId(Number(v))}>
                    <SelectTrigger id="target-master">
                      <SelectValue placeholder="Selecciona una inmobiliaria" />
                    </SelectTrigger>
                    <SelectContent>
                      {masterProjects
                        .filter((m) => m.id !== diffSourceMasterId)
                        .map((m) => (
                          <SelectItem key={m.id} value={m.id.toString()}>
                            {m.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {diffSourceSubs.length > 0 && (
              <div className="space-y-2">
                <Label>Subproyectos a incluir</Label>
                <p className="text-xs text-text-muted">Si no marcas ninguno, se incluyen todos.</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {diffSourceSubs.map((sub) => (
                    <label
                      key={sub.id}
                      htmlFor={`clone-sub-${sub.id}`}
                      className="flex items-center gap-2 rounded-md border border-border p-2 text-sm"
                    >
                      <Checkbox
                        id={`clone-sub-${sub.id}`}
                        checked={diffSubIds.includes(sub.id)}
                        onCheckedChange={() => toggleSub(sub.id)}
                      />
                      <span>{sub.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <EntityCheckboxGrid available={MIGRATION_ENTITIES} selected={diffEntities} onToggle={toggleDiffEntity} />
          </div>
        </TabsContent>
      </Tabs>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void runClone(true);
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Procesando...' : 'Vista previa (Dry run)'}
        </Button>
        <Button
          type="button"
          onClick={() => {
            void runClone(false);
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Ejecutando...' : 'Ejecutar'}
        </Button>
      </div>

      {result && <CloneResultPanel result={result} />}
    </div>
  );
};

export default CloneTab;
